import { z } from 'zod';
import type { Role } from './enums';

/**
 * Надстройки роли (ADR 0086) — третья ось субъекта доступа.
 *
 * Право в портале выдаётся паре «роль + тип контрагента» (ADR 0038). Есть работа, которая в эту
 * пару не помещается: «Оператор (оргтехника)» остаётся штабом своего объекта или сотрудником
 * отдела — со своими заявками, своей площадкой и своим модулем вывоза, — а сверх этого получает
 * набор действий в одном модуле. Роль у учётки одна, и вторая означала бы вторую учётку и второй
 * вход; право у роли `shtab` сделало бы оператором каждый штаб компании.
 *
 * Отсюда надстройка: не роль и не контрагент, а именованный набор прав, который навешивается на
 * учётку поверх её роли. Область при этом **не меняется** — надстройка отвечает на «что человек
 * дополнительно может», а не на «над какими строками»: смешение слоёв дало бы либо 403 на своей же
 * заявке, либо доступ к чужой (ADR 0021).
 */
export const ROLE_ADDONS = ['office_equipment_operator', 'office_equipment_it_approver'] as const;
export const roleAddonSchema = z.enum(ROLE_ADDONS);
export type RoleAddon = (typeof ROLE_ADDONS)[number];

export const roleAddonLabels: Record<RoleAddon, string> = {
  // Подпись называет предмет работы, а не набор прав: в списке учёток её читают рядом с ролью, и
  // «Оператор» без уточнения спорил бы с «Оператором (внешним исполнителем)».
  // Названия приведены к каталогу полномочий (волна В5 плана переработки заявок): человек видит в
  // списке учёток ту же подпись, что администратор выбирает при выдаче набора, — иначе «Оператор
  // (оргтехника)» и «Оргтехника: ведение» читались бы как два разных доступа.
  office_equipment_operator: 'Оргтехника: ведение',
  office_equipment_it_approver: 'Оргтехника: ИТ-служба',
};

export const roleAddonColors: Record<RoleAddon, string> = {
  // Цвет отличается от цветов ролей: надстройка — не роль, и в списке учёток они не должны
  // выглядеть одинаковыми пометками.
  office_equipment_operator: 'purple',
  // Своим цветом, а не тем же: у этой надстройки есть то, чего нет у первой, — сквозная область
  // модуля, и в списке учёток две пометки не должны читаться как одна и та же выдача.
  office_equipment_it_approver: 'magenta',
};

/**
 * К каким базовым ролям надстройка прикрепляется.
 *
 * Таблица, а не проверка по месту: «оператор оргтехники» на арендодателе ТС — это доступ, которого
 * никто не назначал, и запрет должен быть виден там же, где список надстроек. Следующая надстройка
 * добавляется строкой здесь, и компилятор потребует ответить, кому она положена.
 *
 * Список начинался с двух ролей по постановке: оргтехникой в компании занимаются на площадке (штаб)
 * и в офисе (отдел). Дописанная роль — это всегда строка в этой таблице, а не переделка; так сюда
 * приехали площадка (`site`, ADR 0113) и офисная пара «Ведения» (Р5 плана профилей).
 *
 * **Площадка (`site`) дописана шагом prepare этапа 8** (ADR 0113), и обе строки получили её по
 * одной причине: после перевода учёток `shtab` в этой таблице не останется никого. Роль у
 * надстройки спрашивают дважды — `can` проверяет `canAttachAddon` на каждом праве, а форма учётки
 * не даёт сохранить несовместимую пару, — и не допиши мы роль заранее, перевод отобрал бы
 * надстройку у переведённого оператора молча, а первая же правка его карточки отвечала бы 400.
 * Расширением доступа строка не является: до перевода роль `site` не назначена никому, а сама по
 * себе надстройка прав не даёт — её выдают поимённо.
 */
export const ROLE_ADDON_BASE_ROLES: Record<RoleAddon, readonly Role[]> = {
  /*
   * `manager` и `dispatcher` дописаны этапом Э7 плана профилей оргтехники (Р5, миграция B): ведение
   * оргтехники бывает и ЦЕНТРАЛИЗОВАННЫМ — в офисе, у тех же людей, к кому и так идут со сломанным
   * принтером. Правило, по которому роль сюда попадает, записано в Р5 и проверяется тестом: ось
   * роли обязана иметь клетку не `forbidden` по каждому модулю состава (у обеих ось `none`, клетки
   * `service`/`officeEquipment × none` — `global`), итог обязан удовлетворять `PERMISSION_REQUIRES`,
   * и набор обязан что-то роли давать.
   *
   * СТРОКА ЕДЕТ ВМЕСТЕ С `grant_roles`, А НЕ ВМЕСТО НЕЁ (§9.3 п. 1). Пока `role-addons.ts` жив,
   * страж каталога (`grants-catalog.db.test.ts`) строит ожидание строк `grant_roles` отсюда:
   * разъехавшись, надстройка и набор дали бы одному человеку разные права в зависимости от того,
   * каким источником пришёл доступ.
   *
   * У ДИСПЕТЧЕРА У ЭТОГО РАСШИРЕНИЯ ЕСТЬ НАЗВАННОЕ СЛЕДСТВИЕ, И ОНО ПРИНЯТО. Откат статуса заявки
   * спрашивается парой `requests.rollbackStatus` **и** `serviceRequests.status`: первое у него есть
   * по роли, второго не было. Выдав «Ведение», мы вместе с ходом заявки открываем и откат — в этом
   * модуле, не во всех сразу. Умолчание плана (Р5) — «откатывает тот, кому звонят», та же причина,
   * по которой право у диспетчера есть в заказе ТС.
   *
   * ЧЕГО СТРОКА НЕ ДЕЛАЕТ: не выдаёт набор никому. Она отвечает на «кому МОЖНО выдать», и выдача
   * остаётся действием администратора со своей записью в реестре.
   */
  office_equipment_operator: ['shtab', 'site', 'department', 'manager', 'dispatcher'],
  /*
   * Отдел ИТ сидит в офисе, но «айтишник на площадке» — обычный случай для строительной компании,
   * и обе роли получают надстройку на равных. Базовая роль здесь важна меньше обычного: область
   * модуля надстройка задаёт сама (`ADDON_MODULE_WIDE_SCOPE`).
   *
   * `manager`/`dispatcher` сюда НЕ дописаны, и это решение Р5, а не забытая строка: ИТ-служба
   * остаётся на ролях с осью. Набор и так даёт ей сквозную область модуля, а роль без оси сделала
   * бы глобальной ещё и работу исполнителя — назначение перестало бы что-либо открывать.
   */
  office_equipment_it_approver: ['shtab', 'site', 'department'],
};

/** Можно ли навесить надстройку на эту роль. Учётка без роли не получает надстроек вовсе. */
export function canAttachAddon(role: Role | null | undefined, addon: RoleAddon): boolean {
  return !!role && ROLE_ADDON_BASE_ROLES[addon].includes(role);
}

/**
 * Надстройки учётки — набор, а не одно значение: ничто не мешает одному человеку отвечать и за
 * оргтехнику, и за то, что появится следующим. Предел стоит от опечатки в клиенте, а не от
 * рабочего случая.
 */
export const roleAddonsSchema = z.array(roleAddonSchema).max(ROLE_ADDONS.length);

/**
 * Модули, в которых надстройка снимает сужение области (план модернизации оргтехники, Р54).
 *
 * Это изменение решения 2 ADR 0086 («надстройка добавляет действия и не трогает область»), и
 * сделано оно ровно одним способом — таблицей, а не проверкой по месту. Причина в самой работе
 * согласующего: виза ИТ решает, звать ли внешний сервис, и решение это принимается по всем
 * заявкам компании. Согласующий, видящий только свой отдел, подписывать не может ничего.
 *
 * Расширение **всегда модульное**. Учётка со всеми объектами (способ, который предлагал ADR 0086)
 * расширила бы область во всех модулях сразу: ИТ-шник увидел бы каждую заявку на вывоз мусора и
 * каждый заказ техники. Здесь же в вывозе мусора, заказе ТС и путевых листах он остаётся тем, кем
 * был по своей роли, — это закреплено отрицательным тестом, а не только словами.
 *
 * Пустой список у оператора оргтехники — не забытая строка, а решение 2 ADR 0086 в неизменном
 * виде: он ведёт свою площадку и свой отдел.
 */
export const ADDON_SCOPE_MODULES = ['serviceRequests', 'officeEquipment'] as const;
export type AddonScopeModule = (typeof ADDON_SCOPE_MODULES)[number];

export const ADDON_MODULE_WIDE_SCOPE: Record<RoleAddon, readonly AddonScopeModule[]> = {
  office_equipment_operator: [],
  office_equipment_it_approver: ['serviceRequests', 'officeEquipment'],
};

/**
 * Видит ли субъект модуль целиком, минуя область своей роли. Спрашивается по надстройкам учётки, а
 * не по праву: право отвечает на «что можно делать», область — на «над какими строками», и
 * смешение слоёв даёт либо 403 на своей же заявке, либо доступ к чужой (ADR 0021).
 */
export function hasModuleWideScope(
  addons: readonly RoleAddon[] | null | undefined,
  module: AddonScopeModule,
): boolean {
  return (addons ?? []).some((addon) => ADDON_MODULE_WIDE_SCOPE[addon]?.includes(module));
}

/**
 * Совместим ли набор надстроек с ролью — вопрос формы учётки и сервера сразу. Возвращает первую
 * несовместимую: сообщение об ошибке должно называть виновника, а не «какая-то из надстроек».
 */
export function incompatibleAddon(
  role: Role | null | undefined,
  addons: readonly RoleAddon[],
): RoleAddon | null {
  return addons.find((addon) => !canAttachAddon(role, addon)) ?? null;
}
